import React, { useState } from 'react';
import { useMutation, gql } from '@apollo/client';
import apolloClient from '../../lib/apolloClient';
import { DocumentArrowDownIcon, ArrowRightIcon, LifebuoyIcon, EnvelopeIcon, CheckBadgeIcon, ClockIcon, DocumentPlusIcon } from '@heroicons/react/24/outline';
import { useSnackbar } from 'notistack';
import { S3Image } from '../../lib/s3';
import DocumentUploadModal from '../Vault/DocumentUploadModal';

const REQUEST_CAT_PLAN_EMAIL = gql`
  mutation RequestCatPlanEmail {
    request_cat_plan_email {
      success
      message
    }
  }
`;

const CatastropheTile = ({
  catastropheData,
  catastrophicPlan,
  isRj,
  legacyCatPlanStatus,
  lastCatPlanDownloadedDate,
  catPlanFilesUnavailable,
  templateInfo,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [showUnavailableModal, setShowUnavailableModal] = useState(false);
  const [showFileModal, setShowFileModal] = useState(false);
  const [requestCatPlanEmail, { loading: emailLoading }] = useMutation(REQUEST_CAT_PLAN_EMAIL, { client: apolloClient });

  // Provide defaults if catastropheData is undefined
  const safeData = catastropheData || {};

  // Plan status: prefer new UserPlanProgress if available, fall back to legacy
  const status = catastrophicPlan?.status || safeData?.status || 'not_started';
  const completionPercentage = catastrophicPlan?.completionPercentage ?? safeData?.completionPercentage ?? 0;
  const currentSectionName = safeData?.currentSectionName || '';

  // Agreement status: derived from legacy continuity builder
  const agreementStatus = legacyCatPlanStatus || null; // 'filed' | 'approved' | null
  const isApproved = agreementStatus === 'approved';
  const isFiled = agreementStatus === 'filed';
  const planComplete = status === 'completed';
  const canFileAgreement = planComplete && !agreementStatus;

  const showWarning = status === 'not_started' || completionPercentage === 0;

  const handleEmailCopy = () => {
    if (catPlanFilesUnavailable) {
      setShowUnavailableModal(true);
      return;
    }
    requestCatPlanEmail().then(({ data }) => {
      const result = data?.request_cat_plan_email;
      if (result?.success) {
        enqueueSnackbar(result.message, { variant: 'success' });
      } else if (result?.message === 'catastrophic_plans_files_unavailable') {
        setShowUnavailableModal(true);
      } else {
        enqueueSnackbar(result?.message || 'Something went wrong. Please try again.', { variant: 'error' });
      }
    }).catch(() => {
      enqueueSnackbar('Something went wrong. Please try again.', { variant: 'error' });
    });
  };

  const handleDownloadClick = (e) => {
    e.preventDefault();
    window.location.href = '/succession/catastrophe/download';
  };

  return (
    <div className="bg-succession-bg-primary shadow-succession-md rounded-lg p-6 border border-succession-border transition-theme">
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-lg font-medium text-succession-text-primary transition-theme">Catastrophe Plan</h3>
        <LifebuoyIcon className="h-6 w-6 text-succession-primary transition-theme" />
      </div>

      <div className="mt-4 mb-5">
        <div className="relative pt-1">
          {showWarning ? (
            <>
              <div>
                <span className="text-xs font-semibold inline-block py-2 px-3 rounded-md text-succession-error bg-succession-error-bg transition-colors duration-300">
                  Your business may not recover from a catastrophic event
                </span>
              </div>
              <div className="flex items-center justify-center h-28 mb-2">
                <img src={S3Image('no_catastrophic_plan.png')} alt="No catastrophic plan" className="w-auto object-contain" style={{ height: '7.5rem' }} />
              </div>
            </>
          ) : (
            <>
              <div className="flex mb-2 items-center justify-between">
                <div>
                  <span className={`text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full transition-theme
                    ${status === 'completed' ? 'text-succession-success bg-succession-success-bg' :
                     status === 'in_progress' ? 'text-succession-warning bg-succession-warning-bg' :
                     'text-succession-error bg-succession-error-bg'}`}>
                    {status === 'in_progress' && currentSectionName ? `Section: ${currentSectionName}` :
                     status === 'completed' ? 'Plan Complete' :
                     'Create your Business Catastrophe Plan'}
                  </span>
                </div>
                <div className="text-right">
                  <span className={`text-xs font-semibold inline-block transition-theme
                    ${status === 'completed' ? 'text-succession-success' :
                     status === 'in_progress' ? 'text-succession-warning' :
                     'text-succession-error'}`}>
                    {completionPercentage}%
                  </span>
                </div>
              </div>
              <div className={`overflow-hidden h-2 mb-4 text-xs flex rounded transition-theme
                ${status === 'completed' ? 'bg-succession-success-bg' :
                 status === 'in_progress' ? 'bg-succession-warning-bg' :
                 'bg-succession-error-bg'}`}>
                <div
                  style={{ width: `${completionPercentage}%` }}
                  className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center transition-theme
                    ${status === 'completed' ? 'bg-succession-success' :
                     status === 'in_progress' ? 'bg-succession-warning' :
                     'bg-succession-error'}`}
                ></div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Agreement sub-status row */}
      {(agreementStatus || planComplete) && (
        <div className="mb-4 flex items-center gap-2">
          <span className="text-xs text-succession-text-secondary">Agreement:</span>
          {isApproved && (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-succession-success bg-succession-success-bg rounded-full px-2 py-0.5">
              <CheckBadgeIcon className="h-3.5 w-3.5" />
              Approved
            </span>
          )}
          {isFiled && !isApproved && (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-succession-warning bg-succession-warning-bg rounded-full px-2 py-0.5">
              <ClockIcon className="h-3.5 w-3.5" />
              Awaiting Approval
            </span>
          )}
          {!agreementStatus && planComplete && (
            <span className="text-xs text-succession-text-secondary">Not filed</span>
          )}
        </div>
      )}

      <div className="mt-6 flex flex-wrap gap-2">
        {status === 'not_started' && (
          <a
            href="/succession/catastrophe"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-succession-primary hover:bg-succession-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-succession-primary transition-theme"
          >
            Start Plan
            <ArrowRightIcon className="ml-2 h-4 w-4" />
          </a>
        )}

        {status === 'in_progress' && (
          <a
            href="/succession/catastrophe"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-succession-primary hover:bg-succession-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-succession-primary transition-theme"
          >
            Continue Plan
            <ArrowRightIcon className="ml-2 h-4 w-4" />
          </a>
        )}

        {status === 'completed' && (
          <a
            href="/succession/catastrophe"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-succession-primary hover:bg-succession-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-succession-primary transition-theme"
          >
            View Plan
          </a>
        )}

        {/* File Agreement CTA — shown when plan is complete and no agreement filed yet */}
        {canFileAgreement && (
          <button
            onClick={() => setShowFileModal(true)}
            className="inline-flex items-center px-4 py-2 border border-succession-border text-sm font-medium rounded-md shadow-sm text-succession-text-secondary bg-succession-bg-primary hover:bg-succession-bg-tertiary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-succession-border transition-theme"
          >
            <DocumentPlusIcon className="mr-2 h-4 w-4" />
            File Agreement
          </button>
        )}

        {/* Email me a copy — shown for RJ users with an approved agreement */}
        {isRj && isApproved && (
          <button
            onClick={handleEmailCopy}
            disabled={emailLoading}
            className="inline-flex items-center px-4 py-2 border border-succession-border text-sm font-medium rounded-md shadow-sm text-succession-text-secondary bg-succession-bg-primary hover:bg-succession-bg-tertiary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-succession-border transition-theme disabled:opacity-60"
          >
            <EnvelopeIcon className="mr-2 h-4 w-4" />
            {emailLoading ? 'Sending…' : 'Email me a copy'}
          </button>
        )}

        {/* Non-RJ completed plan — download PDF */}
        {!isRj && status === 'completed' && (
          <a
            href="/succession/catastrophe/download"
            onClick={handleDownloadClick}
            className="inline-flex items-center px-4 py-2 border border-succession-border text-sm font-medium rounded-md shadow-sm text-succession-text-secondary bg-succession-bg-primary hover:bg-succession-bg-tertiary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-succession-border transition-theme"
          >
            <DocumentArrowDownIcon className="mr-2 h-4 w-4" />
            Download PDF
          </a>
        )}
      </div>

      {/* Vault upload modal for filing agreement */}
      {showFileModal && (
        <DocumentUploadModal
          open={showFileModal}
          onClose={() => setShowFileModal(false)}
          onSuccess={() => setShowFileModal(false)}
          defaultTitle="Catastrophic Plan Agreement"
          defaultCategory="legal"
          defaultSourceType="plan_builder_catastrophic"
          templateInfo={templateInfo}
        />
      )}

      {/* Unavailable modal */}
      {showUnavailableModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={() => setShowUnavailableModal(false)}>
          <div className="bg-succession-bg-primary rounded-lg p-6 max-w-md w-full mx-4 shadow-succession-lg border border-succession-border" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-succession-text-primary mb-2">Agreement Temporarily Unavailable</h3>
            <p className="text-sm text-succession-text-secondary mb-4">
              Your approved catastrophic plan agreement is temporarily unavailable. Please try again later or contact your support team.
            </p>
            <button
              onClick={() => setShowUnavailableModal(false)}
              className="px-4 py-2 text-sm font-medium text-white bg-succession-primary rounded-md hover:bg-succession-primary-hover transition-theme"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CatastropheTile; 